import { useMemo } from 'react'
import { CaseJourneyStripView } from '@/components/case-overview/CaseJourneyStripView'
import { useEntityResolutionCount } from '@/features/case-overview/useEntityResolutionCount'
import { useHypotheses } from '@/features/hypotheses'
import { useCaseSchemaStatus } from '@/features/schema/hooks/useCaseSchemaStatus'
import { useInvestigationsStore } from '@/stores/investigations-store-provider'

interface CaseJourneyStripProps {
  caseId: string
  hasDescription: boolean
  documentCount: number
}

/**
 * Connected case roadmap. Derives the entity-resolution, research and
 * investigation progress signals, then renders the pure
 * {@link CaseJourneyStripView}.
 */
export function CaseJourneyStrip({ caseId, hasDescription, documentCount }: CaseJourneyStripProps) {
  const schema = useCaseSchemaStatus(caseId)
  const { pending, isError } = useEntityResolutionCount(caseId)

  const { data: hypotheses } = useHypotheses(caseId)
  const researchRuns = useMemo(
    () => (hypotheses ?? []).filter(h => h.runCount > 0).length,
    [hypotheses]
  )

  const saved = useInvestigationsStore(s => s.saved)
  const investigationsInReport = useMemo(
    () => saved.filter(i => i.includedInReport).length,
    [saved]
  )

  return (
    <CaseJourneyStripView
      caseId={caseId}
      schemaStatus={schema}
      hasDescription={hasDescription}
      documentCount={documentCount}
      pendingMatches={pending}
      entityResolutionError={isError}
      researchRuns={researchRuns}
      investigationsTotal={saved.length}
      investigationsInReport={investigationsInReport}
    />
  )
}
